/**
 * Live connections, kept per TAB — outside the component tree.
 *
 * A WebSocket used to live in `useWebSocket`'s component state. Switching to another
 * request tab unmounts the builder: the log was gone, the status read "Idle" again — and
 * the socket itself was never closed, so it kept running with nobody listening (found
 * 19.09.2026). Two WebSocket tabs even shared one builder instance.
 *
 * Here a connection belongs to the tab id it was opened from and lives exactly as long
 * as that tab: `pruneLiveConnections(openTabIds)` (one call site, in `useTabs`) closes
 * whatever belongs to a tab that is gone — closed, "close all", workspace switch.
 *
 * Plain module + subscribe, read with `useSyncExternalStore` (see `useWebSocket`).
 */
import { WebSocketClient } from './websocketClient';
import { NativeWebSocketClient } from './websocketClientNative';
import { isTauri } from './isTauri';

/** Oldest messages are dropped past this — a chatty feed must not grow without bound. */
export const MAX_LOG_MESSAGES = 1000;

/** @typedef {'idle'|'connecting'|'connected'|'disconnected'|'error'} LiveStatus */

const IDLE = Object.freeze({ status: 'idle', messages: Object.freeze([]), dropped: 0 });

/**
 * What the close code of a WebSocket means (RFC 6455 §7.4.1 + the IANA registry) — shown
 * beside the number in the log ("1000 Normal Closure"), the way a person reads it.
 */
export const WS_CLOSE_CODES = Object.freeze({
  1000: 'Normal Closure', 1001: 'Going Away', 1002: 'Protocol Error', 1003: 'Unsupported Data',
  1005: 'No Status Received', 1006: 'Abnormal Closure', 1007: 'Invalid Payload Data', 1008: 'Policy Violation',
  1009: 'Message Too Big', 1010: 'Mandatory Extension', 1011: 'Internal Error', 1012: 'Service Restart',
  1013: 'Try Again Later', 1014: 'Bad Gateway', 1015: 'TLS Handshake Failed',
});

export function describeClose(code, reason) {
  if (code === undefined || code === null) return null;
  const name = WS_CLOSE_CODES[code] || (code >= 4000 ? 'Application Code' : 'Unknown');
  const why = String(reason || '').trim()
    || (code === 1000 ? 'Connection was closed successfully.' : code === 1006 ? 'The connection dropped without a close frame.' : '');
  return { code, name, reason: why };
}

const entries = new Map(); // tabId → { snapshot, client }
const listeners = new Set();
let counter = 0;
const nextId = () => `ws-msg-${Date.now()}-${++counter}`;

const emit = () => listeners.forEach((fn) => fn());
const keyOf = (tabId) => (tabId === null || tabId === undefined ? null : String(tabId));

function patch(key, change) {
  const entry = entries.get(key);
  if (!entry) return;
  entry.snapshot = { ...entry.snapshot, ...change }; // a NEW object: useSyncExternalStore compares by identity
  emit();
}

function append(key, message) {
  const entry = entries.get(key);
  if (!entry) return;
  const all = [...entry.snapshot.messages, message];
  const overflow = Math.max(0, all.length - MAX_LOG_MESSAGES);
  patch(key, { messages: overflow ? all.slice(overflow) : all, dropped: entry.snapshot.dropped + overflow });
}

/**
 * A row about the CONNECTION — connected / disconnected / error — in the same log as the
 * messages. They used to exist only as toasts, so the log could not say when, or why, a
 * feed stopped. `detail` is what we honestly know (a browser WebSocket never exposes the
 * handshake's headers; a close gives its code and reason).
 */
function appendEvent(key, event, text, detail = null) {
  append(key, { id: nextId(), kind: 'event', event, data: text, detail, timestamp: Date.now() });
}

export function subscribeLiveConnections(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** `{ status, messages, dropped }` — a stable object until something changes. */
export function getLiveConnection(tabId) {
  const entry = entries.get(keyOf(tabId));
  return entry ? entry.snapshot : IDLE;
}

export function isLive(tabId) {
  const { status } = getLiveConnection(tabId);
  return status === 'connected' || status === 'connecting';
}

/** What a connection may be given. Everything is optional; the browser client ignores what it cannot do. */
export const DEFAULT_WS_SETTINGS = Object.freeze({
  verifyCertificate: true,
  handshakeTimeoutMs: 0,      // 0 = the client's default (30 s)
  maxMessageSizeMb: 0,        // 0 = the client's default
  reconnectAttempts: 0,       // 0 = never reconnect
  reconnectIntervalMs: 5000,
});

/** Which client speaks: the Rust one on the desktop (headers, handshake details, TLS and size options), the browser's elsewhere. */
function createClient(url, handlers, config) {
  const Client = isTauri() ? NativeWebSocketClient : WebSocketClient;
  return new Client(url, handlers.onMessage, handlers.onOpen, handlers.onClose, handlers.onError, config);
}

/**
 * The handshake for the log, with every request header whose value came from a secret
 * {{variable}} shown as the log-safe text instead (`shownHeaders`: lower-case name -> text).
 */
function maskHandshake(handshake, shownHeaders) {
  if (!shownHeaders || !Object.keys(shownHeaders).length || !Array.isArray(handshake.requestHeaders)) return handshake;
  const requestHeaders = handshake.requestHeaders.map(([name, value]) => {
    const lower = String(name).toLowerCase();
    return Object.prototype.hasOwnProperty.call(shownHeaders, lower) ? [name, shownHeaders[lower]] : [name, value];
  });
  return { ...handshake, requestHeaders };
}

/** One attempt on an EXISTING entry — the first connect and every reconnect. The log stays. */
function openSocket(key, entry) {
  // `url` is what the socket opens; `shown` is what the log says — the same, unless a
  // secret {{variable}} went into the url (then it stays `{{name}}` in the log).
  const { url, settings } = entry;
  const shown = entry.displayUrl ?? url;
  // Callbacks of a REPLACED or pruned client must not touch the entry that came after it.
  const mine = () => entries.get(key) === entry && entry.client === client; // eslint-disable-line no-use-before-define
  let client = null;

  /** The connection is gone and nobody asked for that: try again, if the settings say so. */
  const lost = () => {
    if (entry.retriesLeft <= 0) return false;
    const attempt = settings.reconnectAttempts - entry.retriesLeft + 1;
    entry.retriesLeft -= 1;
    const seconds = Math.round(settings.reconnectIntervalMs / 100) / 10;
    appendEvent(key, 'reconnecting', `Reconnecting in ${seconds} s (attempt ${attempt} of ${settings.reconnectAttempts})`, { url: shown });
    patch(key, { status: 'connecting' });
    entry.retryTimer = setTimeout(() => { entry.retryTimer = null; if (entries.get(key) === entry) openSocket(key, entry); }, settings.reconnectIntervalMs);
    return true;
  };

  try {
    client = createClient(url, {
      onMessage: (msg) => { if (mine()) append(key, { id: nextId(), kind: 'message', direction: 'in', data: msg.data, timestamp: msg.timestamp }); },
      // the desktop client hands over the handshake as it really went; a browser socket has nothing to show
      onOpen: (handshake) => {
        if (!mine()) return;
        entry.retriesLeft = settings.reconnectAttempts;
        appendEvent(key, 'connected', `Connected to ${shown}`, { url: shown, handshake: handshake?.status ? maskHandshake(handshake, entry.shownHeaders) : null });
        patch(key, { status: 'connected' });
      },
      onClose: (e) => {
        if (!mine()) return;
        entry.client = null;
        appendEvent(key, 'disconnected', `Disconnected from ${shown}`, { url: shown, close: describeClose(e?.code, e?.reason) });
        if (!lost()) patch(key, { status: 'disconnected' });
      },
      onError: (e) => {
        if (!mine()) return;
        // An ESTABLISHED socket that breaks fires error, then close: the close row says it, with its code.
        if (entry.snapshot.status === 'connected') return;
        entry.client = null;
        appendEvent(key, 'error', `Could not connect to ${shown}`, { url: shown, message: e?.message || '' });
        if (!lost()) patch(key, { status: 'error' });
      },
    }, { headers: entry.headers, options: settings });
  } catch (error) {
    // `new WebSocket()` THROWS for some URLs that look fine (`ws://`, a bad port, ws:// from
    // an https page). The entry is already there as "connecting" — left like that, the tab
    // would be stuck with its URL field and buttons disabled until it is closed.
    entry.snapshot = { ...entry.snapshot, status: 'error', error: String(error?.message || error) };
    appendEvent(key, 'error', `Could not connect to ${shown}`, { url: shown, message: String(error?.message || error) });
    return;
  }
  entry.client = client;
  entry.closeQuietly = () => client.close();
  emit();
}

const stopRetrying = (entry) => { if (entry?.retryTimer) { clearTimeout(entry.retryTimer); entry.retryTimer = null; } };

/**
 * Open (or re-open) the tab's WebSocket. A new session starts with an empty log.
 * @param {{ headers?: Record<string, string>, settings?: Partial<typeof DEFAULT_WS_SETTINGS>, displayUrl?: string, shownHeaders?: Record<string, string> }} [config]
 *   `displayUrl`: what the log shows instead of `url` (a secret {{variable}} kept unresolved);
 *   `shownHeaders`: the same for handshake request headers, by lower-case name.
 */
export function connectWebSocket(tabId, url, config = {}) {
  const key = keyOf(tabId);
  if (key === null) return;
  const old = entries.get(key);
  stopRetrying(old);
  if (old?.client) { old.client = null; old.closeQuietly?.(); }

  const settings = { ...DEFAULT_WS_SETTINGS, ...(config.settings || {}) };
  const entry = {
    snapshot: { status: 'connecting', messages: [], dropped: 0 },
    client: null, closeQuietly: null, url, displayUrl: config.displayUrl ?? url, shownHeaders: config.shownHeaders || null,
    headers: config.headers || {}, settings, retriesLeft: settings.reconnectAttempts, retryTimer: null,
  };
  entries.set(key, entry);
  openSocket(key, entry);
}

/** The user's "Disconnect": the socket goes (and any reconnect that was waiting), the log stays readable. */
export function disconnectLive(tabId) {
  const key = keyOf(tabId);
  const entry = entries.get(key);
  if (!entry) return;
  const { client } = entry;
  const waiting = !!entry.retryTimer;
  const wasConnected = entry.snapshot.status === 'connected';
  stopRetrying(entry);
  entry.retriesLeft = 0;
  entry.client = null;
  client?.close();
  const url = entry.displayUrl || entry.url || '';
  if (client && wasConnected) appendEvent(key, 'disconnected', url ? `Disconnected from ${url}` : 'Disconnected', { url, close: describeClose(1000, 'Closed from Apiboo.') });
  else if (client || waiting) appendEvent(key, 'disconnected', 'Connection attempt cancelled', { url });
  patch(key, { status: 'disconnected' });
}

/** `shownText`: what the log row shows (defaults to `text`; a secret {{variable}} kept unresolved). */
export function sendLive(tabId, text, shownText = text) {
  const key = keyOf(tabId);
  const entry = entries.get(key);
  if (!entry?.client) return false;
  // `send` is a no-op while the socket is closing — do not log what never left.
  if (entry.client.send(text) === false) return false;
  append(key, { id: nextId(), kind: 'message', direction: 'out', data: shownText, timestamp: Date.now() });
  return true;
}

export function clearLiveLog(tabId) {
  patch(keyOf(tabId), { messages: [], dropped: 0 });
}

// The unsent message of a tab. Not part of the snapshot: only the composer reads it, once, when it mounts.
const drafts = new Map();
export const getLiveDraft = (tabId) => drafts.get(keyOf(tabId)) ?? '';
export function setLiveDraft(tabId, text) {
  const key = keyOf(tabId);
  if (key === null) return;
  if (text) drafts.set(key, text); else drafts.delete(key);
}

/** The tab is gone: close the socket and forget the log. */
export function closeLiveConnection(tabId) {
  const key = keyOf(tabId);
  const entry = entries.get(key);
  if (!entry) return;
  const { client } = entry;
  stopRetrying(entry);
  entry.client = null;
  entries.delete(key);
  client?.close();
  emit();
}

/** Keep only what belongs to a tab that is still open. */
export function pruneLiveConnections(openTabIds) {
  const open = new Set([...openTabIds].map(String));
  [...entries.keys()].filter((key) => !open.has(key)).forEach(closeLiveConnection);
  [...drafts.keys()].filter((key) => !open.has(key)).forEach((key) => drafts.delete(key));
}

/** Tests only. */
export function resetLiveConnectionsForTest() {
  [...entries.keys()].forEach(closeLiveConnection);
  listeners.clear();
}
